<script setup lang="ts">
    import { CardMenu } from '@/components/ui/card';
    import { columns } from '@/tables/columns/holidays';
    import { Head } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateHoliday from '@/components/holidays/CreateHoliday.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { BreadcrumbItem, NavItem } from '@/types';

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'menu.salary_setup',
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'holidays.page_header',
            href: route('v2.salary.holidays.index'),
            permissionLevel: Permission.HOLIDAYS_VIEW,
        },
        {
            title: 'work-calendars.page_header',
            href: route('v2.salary.work-calendars.index'),
            permissionLevel: Permission.WORK_CALENDARS_VIEW,
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.HOLIDAYS],
        queryFn: getHolidays,
    });

    async function getHolidays() {
        const { data } = await axios.get(route('v2.salary.holidays.table'));
        return data.data;
    }
</script>

<template>
    <Head :title="$t('holidays.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        header-class="w-full"
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.HOLIDAYS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-sorting="[
                    {
                        id: 'name',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardMenu :items="navItems" />
                </template>
                <template
                    #actions
                    v-if="hasPermission(Permission.HOLIDAYS_CREATE)"
                >
                    <DataTableToolbarActions name="holidays">
                        <CreateHoliday v-if="hasPermission(Permission.HOLIDAYS_CREATE)" />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
